import dayjs from 'dayjs'
import { Fragment, useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Controller, useFieldArray } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { Alert } from '../../components/alerts/Alert'
import { FormReactDatePicker } from '../../components/forms/datePicker/FormReactDatePicker'
import { FormInput } from '../../components/forms/input/FormInput'
import { FormSelect } from '../../components/forms/select/FormSelect'
import ComponentePrivado from '../../components/roles/ComponentePrivado'
import useHasPermiso from '../../hooks/autenticacion/useHasPermiso'
import { useRecuperarOpcCatalogoQuery } from '../../redux/api'
import {
  useActualizarRecordatorioMutation,
  useEliminarRecordatorioPorIdMutation,
  useGuardarRecordatorioMutation,
} from '../../redux/api/recordatorios.api'
import { AppCodigosCatalogo, AppConstantes } from '../../utils/Constantes'
import { Roles } from '../../utils/Roles'
import { capitalInicial } from '../../utils/toCapital'
import { style } from '../recordatorios/models/recordatorioForm.type'
import { Recordatorio } from './models'

const OPCIONES_BLOQUEADAS = 'BAJA'

type TProps = {
  isModoEditar: boolean
  getValues: any
  esIndefinida?: boolean
  fechaReferencia?: string
  idRelacionado?: string
  tipo: string //PERTENECE A UNA TAREA O A UNA AUTORIZACION: 'TAREA', 'AUTORIZACION'
  setTotalRecordatorios?: (arg: number) => void
  isModalRecordatorios?: boolean
  actualizaRecordatorios?: () => void
  estatus?: string
  esAtributoAnidado?: boolean
  indexObjetoPadre?: number
  control: any
  register: any
  watch?: any
  trigger?: any
  errors: any
  nombreUseFieldArray?: string
  esRecurrente?: boolean
  isRemoverRecordatorios?: boolean
}

/**
 * Componente reutilizable de recordatorios (useFieldArray).
 * Soporta dos modos:
 * - Normal (`nombreUseFieldArray`): recordatorios en formulario de autorizacion/condicionante.
 * - Anidado (`esAtributoAnidado`): recordatorios dentro de tareas (sub-formulario).
 * Cada fila permite unidad+cantidad (periodo) O fecha personalizada.
 * En modo editar, persiste/actualiza/elimina via API en tiempo real (por fila).
 */
export const Recordatorios = ({
  isModoEditar,
  getValues,
  esIndefinida,
  fechaReferencia,
  idRelacionado,
  tipo,
  setTotalRecordatorios,
  isModalRecordatorios,
  actualizaRecordatorios,
  estatus,
  esAtributoAnidado = false,
  indexObjetoPadre,
  control,
  register,
  watch,
  trigger,
  errors,
  nombreUseFieldArray = 'recordatorios',
  esRecurrente = false,
  isRemoverRecordatorios = false,
}: TProps) => {
  const { hasPermiso } = useHasPermiso()

  const tipoRol =
    tipo === AppConstantes.CVE_RECORDATORIO_AUTORIZACION
      ? Roles.roleAutorizacionesCrear
      : Roles.roleTareasCrear

  const usuarioPermiso = hasPermiso([tipoRol])

  const { data: catUnidadRecordatorio } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.RECORDATORIO_UNIDAD
  )
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: esAtributoAnidado ? `tareas[${indexObjetoPadre}].recordatorios` : nombreUseFieldArray,
  })
  const watchRecordatorios = watch(
    esAtributoAnidado ? `tareas.${indexObjetoPadre}.recordatorios` : nombreUseFieldArray
  )
  const [renderizarCamposAnidadosRecordatorios, setRenderizarCamposAnidadosRecordatorios] =
    useState(false)

  const [guardarRecordatorio] = useGuardarRecordatorioMutation()
  const [actualizarRecordatorio] = useActualizarRecordatorioMutation()
  const [eliminarRecordatorioPorId] = useEliminarRecordatorioPorIdMutation()

  const ref = useRef<HTMLSpanElement>(null)

  const enviarAlFinal = () => {
    ref.current?.scrollIntoView({ behavior: 'smooth' })
  }
  useEffect(() => {
    isRemoverRecordatorios && remove()
  }, [isRemoverRecordatorios, remove])

  useEffect(() => {
    if (setTotalRecordatorios) {
      setTotalRecordatorios(fields.length)
    }
    if (!isModoEditar && esIndefinida && watchRecordatorios.length >= 1) {
      const arrEliminar: number[] = []
      watchRecordatorios.forEach((r: any, index: any) => {
        if (
          r.unidad?.valor &&
          r.unidad.valor !== AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
        ) {
          arrEliminar.push(index)
        }
      })
      remove([...arrEliminar])
    }
  }, [fields.length, esIndefinida, renderizarCamposAnidadosRecordatorios, isModoEditar])

  /**
   * @description Agrega una nueva fila para establecer un recordatorio
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 02/03/2023
   */
  const agregarRecordatorioExtra = () => {
    trigger(
      esAtributoAnidado ? `tareas.${indexObjetoPadre}.recordatorios` : nombreUseFieldArray
    ).then((isValido: boolean) => {
      if (isValido) {
        if (
          (!esAtributoAnidado && !errors[nombreUseFieldArray]) ||
          (esAtributoAnidado && !errors.tareas)
        ) {
          append({
            unidad: null,
            cantidad: '',
          })
        }
      }
    })
  }

  /**
   * @description Genera objeto recordatorio que se guarda o actualiza
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 24/05/2023
   * @param i index del recordatorio (unicamente para recordatorios de tareas guardados
   *          desde el modal de tareas)
   * @returns Objeto generado del recordatorio
   */
  const generarObjetoRecordatorio = (recordatorio: any) => {
    const r: Recordatorio = {
      ...recordatorio,
      idRelacionado: idRelacionado,
      cantidad: +recordatorio.cantidad,
      unidad: recordatorio.unidad.valor,
      descUnidad: recordatorio.unidad.etiqueta,
      tipo: tipo,
      descTipo: capitalInicial(tipo),
      fechaReferencia:
        recordatorio.unidad.valor === AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
          ? null
          : dayjs(fechaReferencia).format(AppConstantes.FORMATO_FECHA_POST),
      fechaRecordatorio:
        recordatorio.unidad.valor !== AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
          ? null
          : dayjs(recordatorio.fechaRecordatorio).format(
              AppConstantes.FORMATO_FECHA_RECORDATORIO_POST
            ),
    }
    return r
  }

  /**
   * @description Forma el objeto de un recordatorio, para actualizar o guardar el recordatorio
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 02/03/2023
   * @param i Index del arreglo de campos de recordatorio
   */
  const actualizarGuardar = (i: number) => {
    const nombreFieldValue = esAtributoAnidado
      ? `tareas.${indexObjetoPadre}.recordatorios.${i}`
      : `${nombreUseFieldArray}.${i}`

    const recordatorio = esAtributoAnidado
      ? getValues().tareas[indexObjetoPadre ? indexObjetoPadre : 0].recordatorios[i]
      : getValues()[nombreUseFieldArray][i]

    trigger(nombreFieldValue).then((isValido: boolean) => {
      actualizaRecordatorios && actualizaRecordatorios()
      if (isValido) {
        const r = generarObjetoRecordatorio(recordatorio)

        if (recordatorio.idRecordatorio) {
          const toastId = toast.loading('Actualizando recordatorio', { style })
          actualizarRecordatorio(r)
            .unwrap()
            .then(() => {
              toast.success('Recordatorio actualizado', { id: toastId, style })
              actualizaRecordatorios && actualizaRecordatorios()
            })
            .catch(() => {
              toast.error('Error al actualizar recordatorio', { id: toastId, style })
            })
        } else {
          const toastId = toast.loading('Guardando recordatorio', { style })
          guardarRecordatorio(r)
            .unwrap()
            .then((resp: any) => {
              toast.success('Recordatorio guardado', { id: toastId, style })
              update(i, { ...recordatorio, idRecordatorio: resp.idRecordatorio })
              actualizaRecordatorios && actualizaRecordatorios()
            })
            .catch(() => {
              toast.error('Error al guardar el recordatorio', { id: toastId, style })
            })
        }
      }
    })
  }

  /**
   * @description Elimina un recordatorio
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 02/03/2023
   * @param index Index del arreglo de campos de recordatorio
   */
  const eliminarRecordatorio = (index: number) => {
    const recordatorio = esAtributoAnidado
      ? getValues().tareas[indexObjetoPadre ? indexObjetoPadre : 0].recordatorios[index]
      : getValues()[nombreUseFieldArray][index]
    if (recordatorio.idRecordatorio) {
      const toastId = toast.loading('Eliminando recordatorio', { style })
      eliminarRecordatorioPorId(recordatorio.idRecordatorio)
        .unwrap()
        .then(() => {
          toast.success('Recordatorio eliminado', { id: toastId, style })
          remove(index)
          actualizaRecordatorios && actualizaRecordatorios()
        })
        .catch(() => {
          toast.error('Error el eliminar recordatorio', {
            id: toastId,
            style,
          })
        })
    } else {
      remove(index)
    }
  }

  const elementosUnidad = esIndefinida
    ? catUnidadRecordatorio?.filter(
        (u) => u.valor === AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
      )
    : esRecurrente
    ? catUnidadRecordatorio?.filter(
        (u) => u.valor !== AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
      )
    : catUnidadRecordatorio

  /**
   * @description Genera el nombre de cada uno de los campos que conformar el arrayField
   *
   * @param index: posición en el arreglo de campos
   * @param atributo: nombre del campo/input del formulario
   * @returns nombre del campo como constante
   */
  const nombreAtributo = (index: number, atributo: string) => {
    const atributoNombre = esAtributoAnidado
      ? `tareas[${indexObjetoPadre}].recordatorios[${index}]`
      : `${nombreUseFieldArray}.${index}`
    return `${atributoNombre}.${atributo}` as const
  }

  const generarPopperPlacement = () => {
    return esAtributoAnidado ? 'auto' : 'top-end'
  }

  /**
   * @description Genera el mensaje de error posible para cada uno de los campos del arrayField
   *
   * @param index: posición en el arreglo de campos
   * @param fieldName: nombre del campo/input del formulario
   * @returns elemento JSX que muestra el error del campo
   */
  const errorField = (index: number, atributo: string) => {
    const error = esAtributoAnidado
      ? errors?.tareas?.[indexObjetoPadre ? indexObjetoPadre : 0]?.recordatorios?.[index]
      : errors?.[nombreUseFieldArray]?.[index]
    const mensajeError = error?.[atributo]?.message
    return <p className='text-danger text-end fs-5'>{mensajeError}</p>
  }

  const botoneraRecordatorio = (index: number) => {
    return (
      <div>
        <ComponentePrivado rolMostrarPorPermiso={[tipoRol]}>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip>
                {esAtributoAnidado ? (
                  <>
                    {getValues().tareas[indexObjetoPadre ? indexObjetoPadre : 0].recordatorios[
                      index
                    ]?.idRecordatorio
                      ? 'Actualizar recordatorio'
                      : 'Guardar recordatorio'}
                  </>
                ) : (
                  <>
                    {getValues()[nombreUseFieldArray][index]?.idRecordatorio
                      ? 'Actualizar recordatorio'
                      : 'Guardar recordatorio'}
                  </>
                )}
              </Tooltip>
            }>
            {esAtributoAnidado ? (
              <button
                className={`btn btn-outline-${
                  getValues().tareas[indexObjetoPadre ? indexObjetoPadre : 0].recordatorios[index]
                    ?.idRecordatorio
                    ? 'success'
                    : 'danger'
                } btn-icon btn-circle btn-sm`}
                type='button'
                onClick={() => {
                  actualizarGuardar(index)
                }}
                disabled={estatus === OPCIONES_BLOQUEADAS}>
                <i className='fa-solid fa-check fs-1'></i>
              </button>
            ) : (
              <button
                className={`btn btn-outline-${
                  getValues()[nombreUseFieldArray][index]?.idRecordatorio ? 'success' : 'danger'
                } btn-icon btn-circle btn-sm`}
                type='button'
                onClick={() => {
                  actualizarGuardar(index)
                }}
                disabled={estatus === OPCIONES_BLOQUEADAS}>
                <i className='fa-solid fa-check fs-1'></i>
              </button>
            )}
          </OverlayTrigger>
        </ComponentePrivado>
      </div>
    )
  }
  return (
    <>
      <div>
        {fields.length === 0 ? (
          <div className='d-flex flex-column justify-content-center'>
            <i className='d-flex justify-content-center bi bi-bell font-size-icon'></i>
            <i className='d-flex justify-content-center fs-4 pt-2'>Sin recordatorios</i>
          </div>
        ) : (
          <>
            {!esAtributoAnidado && (
              <Alert
                mensaje={`Un recordatorio se establece indicando la unidad y cantidad de tiempo, en referencia a la fecha de ${
                  tipo === 'TAREA' ? 'termino' : 'vencimiento'
                }. ${
                  !esRecurrente
                    ? 'O selecciona una fecha y hora para un recordatorio personalizado.'
                    : ''
                }`}
                dismissible={true}
                anexo={
                  esRecurrente ? 'Estos recordatorios se aplicarán a cada una de las tareas.' : ''
                }
              />
            )}
            <div
              id='div-modal-recordatorios'
              className={`overflow-auto min-h-100px ${
                isModalRecordatorios ? 'mh-425px' : 'mh-250px'
              }  `}>
              <div className={`row m-0 p-0 ${esAtributoAnidado && 'justify-content-center'}`}>
                <div className='col-4'>
                  <label className='form-label fs-4 mb-0'>
                    Unidad <span className='text-danger'>*</span>
                  </label>
                </div>
                <div className='col-4'>
                  <label className='form-label fs-4 mb-0'>
                    Cantidad <span className='text-danger'>*</span>
                  </label>
                </div>
              </div>
              <div>
                {fields.map((field: any, index: number) => (
                  <Fragment key={field.id}>
                    <div
                      className={`row mx-0 mt-5 ${esAtributoAnidado && 'justify-content-center'}`}>
                      {esAtributoAnidado && <div className='col-1'></div>}

                      <div className='col-4'>
                        <Controller
                          {...{ control }}
                          name={nombreAtributo(index, 'unidad')}
                          render={({ field: { onChange, value } }) => (
                            <FormSelect
                              {...{ register }}
                              {...{ errors }}
                              elementos={elementosUnidad}
                              value={value}
                              placeholder='Ej. Mes'
                              isObligatorio={true}
                              onChange={(e: any) => {
                                onChange(e)
                                esAtributoAnidado &&
                                  setRenderizarCamposAnidadosRecordatorios(
                                    !renderizarCamposAnidadosRecordatorios
                                  )
                              }}
                              isDisabled={estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
                              closeMenuOnScroll={(e) =>
                                e.target === document.getElementById('div-modal-tareas')
                              }
                            />
                          )}
                        />
                        {errorField(index, 'unidad')}
                      </div>
                      {watchRecordatorios[index].unidad?.valor ===
                      AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO ? (
                        <div className='col-4 '>
                          <Controller
                            {...{ control }}
                            defaultValue=''
                            name={nombreAtributo(index, 'fechaRecordatorio')}
                            render={({ field: { onChange, value } }) => (
                              <FormReactDatePicker
                                {...{ register }}
                                {...{ errors }}
                                required={true}
                                name={nombreAtributo(index, 'fechaRecordatorio')}
                                dateFormat='dd/MMM/yyyy HH:mm:ss'
                                placeholderText='Ej. 22/03/2022'
                                onChange={onChange}
                                isClearable={true}
                                showTimeSelect={true}
                                timeFormat='HH:mm:ss'
                                timeIntervals={15}
                                popperPlacement={generarPopperPlacement()}
                                fixedHeight
                                value={value}
                                closeOnScroll={(e) =>
                                  e.target ===
                                  document.getElementById(
                                    `${
                                      esAtributoAnidado
                                        ? 'div-modal-tareas'
                                        : 'div-modal-recordatorios'
                                    }`
                                  )
                                }
                                disabled={estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
                              />
                            )}
                          />
                          {errorField(index, 'fechaRecordatorio')}
                        </div>
                      ) : (
                        <div className='col-4'>
                          <FormInput
                            {...{ register }}
                            {...{ errors }}
                            nombreCampo={nombreAtributo(index, 'cantidad')}
                            placeholder='Ej. 2'
                            maxLength={4}
                            disabled={estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
                          />
                          {errorField(index, 'cantidad')}
                        </div>
                      )}
                      <div className='col-1 p-0 m-0'>
                        <div className='d-flex justify-content-evenly'>
                          {isModoEditar ? <>{botoneraRecordatorio(index)} </> : ''}
                          <div>
                            <ComponentePrivado rolMostrarPorPermiso={[tipoRol]}>
                              <OverlayTrigger
                                placement='top'
                                overlay={<Tooltip>Eliminar recordatorio</Tooltip>}>
                                <button
                                  className='btn btn-outline-danger btn-icon btn-circle btn-sm'
                                  type='button'
                                  title='Eliminar recordatorio'
                                  onClick={() => {
                                    eliminarRecordatorio(index)
                                  }}
                                  disabled={estatus === OPCIONES_BLOQUEADAS}>
                                  <i className='fa-solid fa-minus fs-1'></i>
                                </button>
                              </OverlayTrigger>
                            </ComponentePrivado>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span ref={ref}></span>
                  </Fragment>
                ))}
                <span className='mt-5'></span>
              </div>
            </div>
          </>
        )}
        <div className='d-flex justify-content-center mt-7'>
          <ComponentePrivado rolMostrarPorPermiso={[tipoRol]}>
            <OverlayTrigger placement='top' overlay={<Tooltip>Agregar recordatorio</Tooltip>}>
              <button
                className='btn btn-outline-success btn-icon btn-circle btn-sm '
                type='button'
                onClick={() => {
                  enviarAlFinal()
                  agregarRecordatorioExtra()
                }}
                disabled={estatus === OPCIONES_BLOQUEADAS}>
                <i className='fa-solid fa-plus fs-1'></i>
              </button>
            </OverlayTrigger>
          </ComponentePrivado>
          <span className='mt-5'></span>
        </div>
      </div>
    </>
  )
}
